/** Edit and retire a vendor, in a side drawer - ADR-0212 decision 4 (rename and retire).
 *
 * **A vendor is renamed and retired, never deleted.** Renaming corrects how the name is written;
 * the server refuses a name another vendor holds, so two parties are never merged by a spelling.
 * Retiring keeps every account, meter link and figure that names the vendor, so a past report does
 * not change; it only stops a *new* account being recorded and the vendor being suggested for a bill.
 * Restoring lifts that. The drawer is offered to an Admin or an Approver only, as Add is (the server
 * answers 403 to anyone else, and a control that predictably fails is not drawn: design rule 6).
 *
 * **Every write sends the version the drawer was opened with** (G-P9-04). If someone else changed the
 * vendor meanwhile the server answers 409 `stale_version`; the form then keeps what was typed, says
 * what the vendor now reads (from a fresh read of the register) and offers "Reload and re-apply",
 * which sends the same act again on the version it now reads.
 */

import { useState } from "react";

import { Refused } from "../../api/refusal";
import { useRegisters, useRenameVendor, useRestoreVendor, useRetireVendor } from "../../api/sources";
import type { RegisterVendor } from "../../api/sources";
import { WriteFailed } from "../../components/Loaded";
import { Drawer } from "../../components/ui/Drawer";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { StatusChip } from "../../components/ui/StatusChip";
import styles from "./Vendors.module.css";

export function EditVendorDrawer({
  vendor,
  onClose,
}: {
  vendor: RegisterVendor | undefined;
  onClose: () => void;
}) {
  // Keyed by the vendor, so opening another starts from its own name rather than the last typed one.
  return (
    <Drawer open={vendor !== undefined} onClose={onClose} title="Edit vendor" width="sm">
      {vendor ? <EditForm key={vendor.id} vendor={vendor} onClose={onClose} /> : null}
    </Drawer>
  );
}

type Act = "rename" | "retire" | "restore";

function EditForm({ vendor, onClose }: { vendor: RegisterVendor; onClose: () => void }) {
  const rename = useRenameVendor();
  const retire = useRetireVendor();
  const restore = useRestoreVendor();
  const registers = useRegisters();
  const [name, setName] = useState(vendor.name);
  const [refusal, setRefusal] = useState<unknown>(null);
  // **The version this drawer was opened with**, held here and not read from `vendor` at send time:
  // the register refetches in the background, and a version taken from the refetched row would make
  // the check compare the row with itself.
  const [openedVersion, setOpenedVersion] = useState(vendor.version);
  const [lastAct, setLastAct] = useState<Act>("rename");
  const [reapplying, setReapplying] = useState(false);
  const busy = rename.isPending || retire.isPending || restore.isPending || reapplying;
  const unchanged = name.trim() === vendor.name;

  const send = (act: Act, expectedVersion: number | undefined) => {
    setRefusal(null);
    setLastAct(act);
    const run =
      act === "rename"
        ? rename.mutateAsync({ id: vendor.id, name: name.trim(), expectedVersion })
        : act === "retire"
          ? retire.mutateAsync({ id: vendor.id, expectedVersion })
          : restore.mutateAsync({ id: vendor.id, expectedVersion });
    return run.then(onClose, (error: unknown) => {
      setRefusal(error);
      // What it now reads is a fresh read, not the list this drawer was opened over.
      if (error instanceof Refused && error.isStale) void registers.refetch();
    });
  };

  const reapply = async () => {
    setReapplying(true);
    try {
      const fresh = (await registers.refetch()).data?.vendors.find((v) => v.id === vendor.id);
      if (!fresh) return;
      setOpenedVersion(fresh.version);
      await send(lastAct, fresh.version);
    } finally {
      setReapplying(false);
    }
  };

  return (
    <div className={styles.form} data-testid="edit-vendor">
      <form
        id="rename-vendor"
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void send("rename", openedVersion);
        }}
      >
        <label className={styles.field}>
          <span>Name as it prints on a bill</span>
          <input
            value={name}
            required
            maxLength={200}
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button
          type="submit"
          className={styles.primary}
          disabled={!name.trim() || unchanged || busy}
          data-testid="rename-vendor"
        >
          {rename.isPending ? "Saving…" : "Save name"}
        </button>
      </form>
      <div className={styles.form}>
        {vendor.is_retired ? (
          <>
            <StatusChip tone="neutral" size="sm" testId="vendor-retired-chip">
              Retired
            </StatusChip>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              data-testid="restore-vendor"
              onClick={() => void send("restore", openedVersion)}
            >
              {restore.isPending ? "Restoring…" : "Restore vendor"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className={styles.ghost}
            disabled={busy}
            data-testid="retire-vendor"
            onClick={() => void send("retire", openedVersion)}
          >
            {retire.isPending ? "Retiring…" : "Retire vendor"}
          </button>
        )}
        <InfoPopover infoKey="vendors.retire-vendor" />
      </div>
      <WriteFailed
        error={refusal}
        of="this vendor"
        onReload={() => setRefusal(null)}
        nowReads={`\u201c${vendor.name}\u201d${vendor.is_retired ? ", retired" : ""}`}
        onReapply={() => void reapply()}
        reapplying={reapplying}
      />
    </div>
  );
}
